/* eslint-disable eqeqeq */
/*
This file is essentially a clone of the officeprops package because officeprops
refuses to work in the project (because of unhandleable strict mode exceptions).
Also updating the package to more recent syntax.
*/

import JSZip from 'jszip';
import { DOMParser, XMLSerializer } from 'xmldom';

export default class OFFICEPROPS {
	static get typeConverters() {
		return typeConverters;
	}
	static get properties() {
		return properties;
	}
	static get mimeTypes() {
		return mimeTypes;
	}

	static DEFAULT_APP_XML = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Properties xmlns="http://schemas.openxmlformats.org/officeDocument/2006/extended-properties" xmlns:vt="http://schemas.openxmlformats.org/officeDocument/2006/docPropsVTypes"></Properties>`;
	static DEFAULT_CORE_XML = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><cp:coreProperties xmlns:cp="http://schemas.openxmlformats.org/package/2006/metadata/core-properties" xmlns:dc="http://purl.org/dc/elements/1.1/" xmlns:dcterms="http://purl.org/dc/terms/" xmlns:dcmitype="http://purl.org/dc/dcmitype/" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"></cp:coreProperties>`;
	static DEFAULT_META_XML = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><office:document-meta xmlns:office="urn:oasis:names:tc:opendocument:xmlns:office:1.0" xmlns:meta="urn:oasis:names:tc:opendocument:xmlns:meta:1.0" xmlns:dc="http://purl.org/dc/elements/1.1/" xmlns:xlink="http://www.w3.org/1999/xlink" office:version="1.1"></office:document-meta>`;

	constructor(obj) {
		if (obj instanceof OFFICEPROPS) return obj;

		this.OFFICEPROPSwrapped = obj;
	}

	/** @param {string} officeFile */
	static async getData(officeFile) {
		try {
			const zip = await loadFile(officeFile);

			const files = await getMetadataAsXML(zip);

			return translateMetadata(
				files.map(f => getTextObjectsFromXML(f)),
				properties
			);
		} catch (e) {
			throw new Error(e);
		}
	}

	static async editData(officeFile, metadata) {
		try {
			const newMetaFiles = await getModifiedMetadataAsXml(officeFile, metadata);
			const zip = await loadFile(officeFile);

			if (zip.OPformat == 'office') {
				zip.remove('docProps/core.xml');
				zip.remove('docProps/app.xml');
				zip.file('docProps/core.xml', serializeXML(newMetaFiles[0]));
				zip.file('docProps/app.xml', serializeXML(newMetaFiles[1]));
			} else {
				zip.remove('meta.xml');
				zip.file('meta.xml', serializeXML(newMetaFiles[0]));
			}

			return getBlob();
		} catch (e) {
			throw new Error(e);
		}
	}

	static async removeData(officeFile, metaData) {
		try {
			const zip = await loadFile(officeFile);

			if (zip.OPformat == 'office') {
				zip.remove('docProps/core.xml');
				zip.remove('docProps/app.xml');

				if ('docProps/custom.xml' in zip.files) {
					zip.remove('docProps/custom.xml');
				}

				zip.file('docProps/core.xml', OFFICEPROPS.DEFAULT_CORE_XML);
				zip.file('docProps/app.xml', OFFICEPROPS.DEFAULT_APP_XML);
			} else if (zip.OPformat == 'openoffice') {
				zip.remove('meta.xml');
				zip.file('meta.xml', OFFICEPROPS.DEFAULT_META_XML);
			} else {
				throw new Error('File not valid');
			}

			return getBlob(zip, officeFile);
		} catch (e) {
			throw new Error(e);
		}
	}
}

export { OFFICEPROPS };

/**
 * @param {JSZip} zip
 */
async function getMetadataAsXML(zip) {
	if (zip.OPformat == 'office') {
		return [await getXmlFromZip(zip, 'docProps/core.xml'), await getXmlFromZip(zip, 'docProps/app.xml')];
	} else if (zip.OPformat == 'openoffice') {
		return [await getXmlFromZip(zip, 'meta.xml')];
	}

	return null;
}

/**
 *
 * @param {JSZip} zip
 * @param {string} filename
 * @returns
 */
function getXmlFromZip(zip, filename) {
	return zip
		.file(filename)
		.async('text')
		.then(text => new DOMParser().parseFromString(text, 'text/xml'));
}

/**
 * @param {} officeFile
 * @returns
 */
async function loadFile(officeFile) {
	const zip = await JSZip.loadAsync(officeFile);

	const format = getFormat(zip);

	if (format) {
		zip.OPformat = format;
		return zip;
	}

	throw new Error('File not valid; could not determine the format');
}

/**
 * @param {JSZip} zip
 */
function getFormat(zip) {
	if ('docProps/core.xml' in zip.files && 'docProps/app.xml' in zip.files) {
		return 'office';
	}

	if ('meta.xml' in zip.files) {
		return 'openoffice';
	}

	return false;
}

/**
 *
 * @param {*[]} textObjects
 * @param {*} names
 */
function translateMetadata(textObjects, names) {
	const headingPairsAndParts = [];
	// use a new flattened version
	textObjects = textObjects.flat();

	textObjects.forEach((e, i, a) => {
		if (e.path == 'HeadingPairs/vt:vector/vt:variant/vt:lpstr') {
			headingPairsAndParts.push({
				name: names.hasOwnProperty(e.path) ? names[e.path] : e.path.replace(/ /g, ''),
				length: a[i + 1].value,
				value: [e.value],
			});
		}
		if (e.path == 'TitlesOfParts/vt:vector/vt:lpstr') {
			for (let i = 0; i < headingPairsAndParts.length; i++) {
				if (headingPairsAndParts[i].value.length < headingPairsAndParts[i].length) {
					headingPairsAndParts[i].value.push(e.value);
					break;
				}
			}
		}
	});

	textObjects = textObjects.filter(
		e =>
			e.path != 'HeadingPairs/vt:vector/vt:variant/vt:lpstr' &&
			e.path != 'TitlesOfParts/vt:vector/vt:lpstr' &&
			e.path != 'HeadingPairs/vt:vector/vt:variant/vt:i4'
	);

	const createPropertyOrArray = (object, property, val) => {
		if (object.hasOwnProperty(property)) {
			if (object[property].value instanceof Array) {
				object[property].value.push(val.value);
				object[property].rvalue.push(val.tvalue);
			} else {
				object[property].tvalue = [object[property].tvalue, val.tvalue];
				object[property].value = [object[property].value, val.value];
			}
		} else {
			object[property] = val;
		}
	};

	let editable = {};
	textObjects.forEach(e => {
		if (names.hasOwnProperty(e.path)) {
			let translatedValue = typeConverters[names[e.path].type](e.value);
			createPropertyOrArray(editable, names[e.path].name, {
				value: e.value,
				tvalue: translatedValue,
				xmlPath: e.path,
			});
		} else {
			createPropertyOrArray(editable, e.path, { value: e.value, tvalue: e.value, xmlPath: e.path });
		}
	});

	let readonly = {};
	headingPairsAndParts.forEach(e => {
		readonly[e.name] = { value: e.value, tvalue: e.value };
	});

	return { editable: editable, readOnly: readonly };
}

function getTextObjectsFromXML(xml) {
	return getTextFromNodeList(xml.lastChild.childNodes);
}

/**
 * @param {NodeListOf<HTMLElement>} nodes the NodeList of XML elements; HTMLElement is the closest thing and will work for this situation.
 * @param {string} name
 * @param {{ path: string, value: string }[]} metaObjects
 * @return {{ path: string, value: string }[]} The same metaObjects array that it started with or a new one if one wasn't specified
 */
function getTextFromNodeList(nodes, name = '', metaObjects = []) {
	for (const element of Array.from(nodes)) {
		const currentPath = name + '/' + element.nodeName;
		if (element.childNodes.length == 1 && element.firstChild.nodeType == 3) {
			metaObjects.push({
				path: currentPath.slice(1),
				value: element.firstChild.textContent,
			});
		} else if (element.childNodes.length > 0) {
			// not assigning the metaObjects because, in reality, they are the same array
			// which was mutated; no reassignment necessary
			getTextFromNodeList(element.childNodes, currentPath, metaObjects);
		} else {
			const metaObject = { path: name + '/' + element.nodeName, value: '' };

			if (metaObject.path == 'office:meta/meta:document-statistic' || metaObject.path == 'office:meta/meta:template') {
				for (const attr of Array.from(element.attributes)) {
					metaObjects.push({ path: metaObject.path + '/@' + attr.name, value: attr.value });
				}
			} else {
				metaObjects.push(metaObject);
			}
		}
	}
	return metaObjects;
}

/**
 *
 * @param {Document} xml
 * @param {{ editable: Record<string, Node> }} metadata
 */
function editXML(xml, metadata) {
	for (const object of Object.values(metadata.editable)) {
		const xmlPathParts = object.xmlPath.split('/');

		if (object.xmlPath.includes('@')) {
			const nodes = xml.getElementsByTagName(xmlPathParts.slice(-2, -1)[0]);

			for (const node of nodes) {
				const attr = node.getAttributeNode(xmlPathParts.slice(-1)[0].replace('@', ''));
				attr.value = object.value;
			}
		} else {
			const nodes = xml.getElementsByTagName(xmlPathParts.slice(-1));

			if (nodes.length > 0 && object.xmlPath != '') {
				for (let i = 0; i < nodes.length; i++) {
					const valueToInsert =
						object.value instanceof Array
							? object.value[i < object.value.length ? i : object.value.length - 1]
							: object.value;

					if (nodes[i].firstChild.length > 0 && nodes[i].firstChild.nodeType == 3) {
						nodes[i].firstChild.data = valueToInsert;
					} else {
						nodes[i].appendChild(xml.createTextNode(valueToInsert));
					}
				}
			}
		}
	}

	return xml;
}

async function getModifiedMetadataAsXml(officeFile, metadata) {
	try {
		const zip = await loadFile(officeFile);
		const xmls = await getMetadataAsXML(zip);
		return xmls.map(e => editXML(e, metadata));
	} catch (e) {
		throw new Error(e);
	}
}

/**
 *
 * @param {JSZip} zip
 * @param {*} originalFile
 * @returns
 */
function getBlob(zip, originalFile) {
	if (typeof Buffer != 'undefined' && originalFile instanceof Buffer) {
		return zip.generateAsync({ type: 'nodebuffer' });
	}
	if (typeof Blob != 'undefined' && originalFile instanceof Blob) {
		return zip.generateAsync({ mimeType: originalFile.type, type: 'blob' });
	}
	return zip.generateAsync({ mimeType: getMimeType(getFileExtension(originalFile)), type: 'blob' });
}

/**
 *
 * @param {string} fileExtension
 */
function getMimeType(fileExtension) {
	return fileExtension ? mimeTypes[fileExtension] : false;
}

function getFileExtension(file) {
	const fileParts = file.name.split('.');
	return fileParts.pop();
}

/**
 *
 * @param {Node} xml
 */
function serializeXML(xml) {
	return new XMLSerializer().serializeToString(xml);
}

// true, these are initialized down here, but since it's not being used in the file
// before this point, it should work as intended
const typeConverters = new Proxy(
	{
		default(element) {
			return element;
		},
		Date(element) {
			return new Date(element).toString();
		},
		enumDocSecurity(element) {
			switch (Number(element)) {
				case 0:
					return 'None';
				case 1:
					return 'Document is password protected.';
				case 2:
					return 'Document is recommended to be opened as read-only.';
				case 4:
					return 'Document is enforced to be opened as read-only.';
				case 8:
					return 'Document is locked for annotation.';
				default:
					return 'Unknown';
			}
		},
		bool(element) {
			switch (element) {
				case 'false':
					return 'No';
				case 'true':
					return 'Yes';
				default:
					return 'unknown';
			}
		},
		ISO8691(element) {
			const parts = /^P((\d+Y)?(\d+M)?(\d+W)?(\d+D)?)?(T(\d+H)?(\d+M)?(\d+S)?)?$/.exec(element);

			const numericParts = parts.slice(2).map(p => {
				if (!p) return 0;

				return Number(p.replace(/[^\d]/g, ''));
			});

			const numbersReducedToMinutes = Math.floor(
				(numericParts[0] * 31104000 +
					numericParts[1] * 2592000 +
					numericParts[2] * 604800 +
					numericParts[3] * 246060 +
					numericParts[5] * 3600 +
					numericParts[6] * 60 +
					numericParts[7]) /
					60
			);

			return this.intMinutes(numbersReducedToMinutes);
		},
		intMinutes(element) {
			return `${element} minute${element == 1 ? 's' : ''}`;
		},
	},
	{
		get(target, property) {
			if (!(property in target)) {
				return target.default;
			}
			return target[property];
		},
	}
);

const properties = {
	'cp:category': { name: 'category', type: 'str' },
	Manager: { name: 'manager', type: 'str' },
	'cp:contentStatus': { name: 'contentStatus', type: 'str' },
	'dc:subject': { name: 'subject', type: 'str' },
	HyperlinkBase: { name: 'hyperlinkBase', type: 'str' },
	'Slide Titles': { name: 'slideTitles', type: 'str' },
	Theme: { name: 'theme', type: 'str' },
	Title: { name: 'titles', type: 'str' },
	'dc:title': { name: 'title', type: 'str' },
	'dc:creator': { name: 'creator', type: 'str' },
	'cp:keywords': { name: 'keywords', type: 'str' },
	'dc:description': { name: 'description', type: 'str' },
	'cp:lastModifiedBy': { name: 'lastModifiedBy', type: 'str' },
	'cp:revision': { name: 'revisionNumber', type: 'int' },
	'dcterms:created': { name: 'created', type: 'Date' },
	'dcterms:modified': { name: 'modified', type: 'Date' },
	Template: { name: 'template', type: 'str' },
	TotalTime: { name: 'totalTime', type: 'intMinutes' },
	Pages: { name: 'pages', type: 'int' },
	Words: { name: 'words', type: 'int' },
	Characters: { name: 'characters', type: 'int' },
	Application: { name: 'application', type: 'str' },
	DocSecurity: { name: 'docSecurity', type: 'enumDocSecurity' },
	Lines: { name: 'lines', type: 'int' },
	Paragraphs: { name: 'paragraphs', type: 'int' },
	ScaleCrop: { name: 'scaleCrop', type: 'bool' },
	Company: { name: 'company', type: 'str' },
	LinksUpToDate: { name: 'linksUpToDate', type: 'bool' },
	SharedDoc: { name: 'sharedDoc', type: 'bool' },
	HyperlinksChanged: { name: 'hyperlinksChanged', type: 'bool' },
	AppVersion: { name: 'appVersion', type: 'float' },
	CharactersWithSpaces: { name: 'charactersWithSpaces', type: 'int' },
	Slides: { name: 'slides', type: 'int' },
	Notes: { name: 'notes', type: 'str' },
	HiddenSlides: { name: 'hiddenSlides', type: 'int' },
	'dc:language': { name: 'language', type: 'str' },
	MMClips: { name: 'mmClips', type: 'str' },
	'cp:lastPrinted': { name: 'lastPrinted', type: 'Date' },
	PresentationFormat: { name: 'presentationFormat', type: 'str' },
	Worksheets: { name: 'worksheets', type: 'str' },
	'office:meta/meta:initial-creator': { name: 'creator', type: 'str' },
	'office:meta/dc:creator': { name: 'lastModifiedBy', type: 'str' },
	'office:meta/meta:creation-date': { name: 'created', type: 'Date' },
	'office:meta/dc:date': { name: 'modified', type: 'Date' },
	'office:meta/meta:template': { name: 'template', type: 'str' },
	'office:meta/meta:editing-cycles': { name: 'revision', type: 'int' },
	'office:meta/meta:editing-duration': { name: 'totalTime', type: 'ISO8601' },
	'office:meta/meta:document-statistic/@meta:page-count': { name: 'pages', type: 'str' },
	'office:meta/meta:document-statistic/@meta:paragraph-count': { name: 'paragraphs', type: 'str' },
	'office:meta/meta:document-statistic/@meta:word-count': { name: 'words', type: 'str' },
	'office:meta/meta:document-statistic/@meta:character-count': { name: 'characters', type: 'str' },
	'office:meta/meta:document-statistic/@meta:row-count': { name: 'rows', type: 'str' },
	'office:meta/meta:document-statistic/@meta:non-whitespace-character-count': {
		name: 'whitespaceCharacters',
		type: 'str',
	},
	'office:meta/meta:template/@xlink:href': { name: 'template', type: 'str' },
	'office:meta/meta:template/@xlink:type': { name: 'templateType', type: 'str' },
	'office:meta/meta:document-statistic/@meta:table-count': { name: 'tables', type: 'str' },
	'office:meta/meta:document-statistic/@meta:image-count': { name: 'images', type: 'str' },
	'office:meta/meta:document-statistic/@meta:object-count': { name: 'objects', type: 'str' },
	'office:meta/meta:generator': { name: 'application', type: 'str' },
};

const mimeTypes = {
	//https://stackoverflow.com/questions/4212861/what-is-a-correct-mime-type-for-docx-pptx-etc
	docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
	dotx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.template',
	docm: 'application/vnd.ms-word.document.macroEnabled.12',
	dotm: 'application/vnd.ms-word.template.macroEnabled.12',
	xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
	xlsm: 'application/vnd.ms-excel.sheet.macroEnabled.12',
	xlsb: 'application/vnd.ms-excel.sheet.binary.macroEnabled.12',
	pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
	ppsx: 'application/vnd.openxmlformats-officedocument.presentationml.slideshow',
	ppsm: 'application/vnd.ms-powerpoint.slideshow.macroEnabled.12',
	pptm: 'application/vnd.ms-powerpoint.presentation.macroEnabled.12',
	xltx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.template',
	xltm: 'application/vnd.ms-excel.template.macroEnabled.12',
	potx: 'application/vnd.openxmlformats-officedocument.presentationml.template',
	potm: 'application/vnd.ms-powerpoint.template.macroEnabled.12',
	odt: 'application/vnd.oasis.opendocument.text',
	odp: 'application/vnd.oasis.opendocument.presentation',
	ods: 'application/vnd.oasis.opendocument.spreadsheet',
	ots: 'application/vnd.oasis.opendocument.spreadsheet-template',
	otp: 'application/vnd.oasis.opendocument.presentation-template',
	ott: 'application/vnd.oasis.opendocument.text-template',
};
